import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Users, Trophy, Sparkles, Brain, TrendingUp, User,
  ArrowLeft, Loader2, Medal, Crown, Award, Lock, Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CommunityPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [activeTab, setActiveTab] = useState('oi');
  const [timeFilter, setTimeFilter] = useState('all');
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    // Fetch eligibility on mount
    const fetchEligibility = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/leaderboards/eligibility`, {
          withCredentials: true
        });
        setEligibility(response.data);
      } catch (error) {
        console.error('Error checking eligibility:', error);
      }
    };
    
    if (isAuthenticated) {
      fetchEligibility();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchLeaderboard();
  }, [activeTab, timeFilter]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const endpoint = `/api/leaderboards/${activeTab}?time_filter=${timeFilter}&limit=50`;
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, {
        withCredentials: true
      });
      setLeaderboardData(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) {
      return <Crown className="w-6 h-6 text-yellow-500" />;
    } else if (rank === 2) {
      return <Medal className="w-6 h-6 text-gray-400" />;
    } else if (rank === 3) {
      return <Medal className="w-6 h-6 text-amber-600" />;
    }
    return <span className="text-xl font-bold text-gray-500">#{rank}</span>;
  };

  const getScoreColor = (type) => {
    if (type === 'iqi') return 'from-purple-500 to-pink-500';
    if (type === 'oi') return 'from-blue-500 to-cyan-500';
    return 'from-emerald-500 to-teal-500';
  };

  const tabs = [
    { id: 'iqi', label: 'Empathetic Innovators', icon: Sparkles, desc: 'Highest Interaction Quality' },
    { id: 'oi', label: 'Original Thinkers', icon: Brain, desc: 'Most Original Ideas' },
    { id: 'msa', label: 'Top Innovators', icon: TrendingUp, desc: 'Highest Spark %' }
  ];

  const timeFilters = [
    { value: 'all', label: 'All Time' },
    { value: 'month', label: 'This Month' },
    { value: 'week', label: 'This Week' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Fountainhead Community
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <Trophy className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
            Community Leaderboards
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover and celebrate the most innovative Fountainheads in our community
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center p-6 rounded-xl transition-all ${
                  isActive
                    ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:shadow-md hover:scale-102'
                }`}
              >
                <Icon className={`w-8 h-8 mb-2 ${isActive ? 'text-white' : 'text-purple-600'}`} />
                <div className="text-lg font-bold">{tab.label}</div>
                <div className={`text-sm ${isActive ? 'text-white/90' : 'text-gray-500'}`}>
                  {tab.desc}
                </div>
              </button>
            );
          })}
        </div>

        {/* Time Filter */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg shadow-sm p-2 flex gap-2">
            {timeFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-4 py-2 rounded-md font-medium transition-all ${
                  timeFilter === filter.value
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Leaderboard */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading leaderboard...</p>
          </div>
        ) : leaderboardData?.restricted ? (
          /* Restricted Access - Need more activity */
          <Card className="max-w-2xl mx-auto border-2 border-amber-200 bg-amber-50">
            <CardContent className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-amber-900 mb-3">
                Industry Leaders Board Locked
              </h3>
              <p className="text-amber-700 mb-6 max-w-md mx-auto">
                {leaderboardData.message}
              </p>
              
              {/* Progress Bar */}
              {leaderboardData.eligibility && (
                <div className="max-w-md mx-auto mb-6">
                  <div className="flex justify-between text-sm text-amber-700 mb-2">
                    <span>Your Progress</span>
                    <span>{leaderboardData.eligibility.total_activity} / {leaderboardData.eligibility.required}</span>
                  </div>
                  <Progress 
                    value={(leaderboardData.eligibility.total_activity / leaderboardData.eligibility.required) * 100} 
                    className="h-3 bg-amber-200"
                  />
                  <div className="flex justify-between text-xs text-amber-600 mt-2">
                    <span>{leaderboardData.eligibility.session_count} sessions</span>
                    <span>{leaderboardData.eligibility.evaluation_count} evaluations</span>
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => navigate('/session/new')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Start IF Session
                </Button>
                <Button
                  onClick={() => navigate('/evaluate')}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-100"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Evaluate Paper
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-6 h-6 text-purple-600" />
                {leaderboardData?.title || 'Leaderboard'}
                <Badge variant="secondary" className="ml-auto">
                  {leaderboardData?.total_count || 0} Fountainheads
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leaderboardData && leaderboardData.leaderboard.length > 0 ? (
                <div className="space-y-4">
                  {leaderboardData.leaderboard.map((entry) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-4 p-4 rounded-lg transition-all cursor-pointer ${
                        entry.rank <= 3
                          ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 hover:shadow-lg'
                          : 'bg-gray-50 hover:bg-gray-100 hover:shadow-md'
                      }`}
                      onClick={() => navigate(`/profile/${entry.username}`)}
                    >
                      {/* Rank */}
                      <div className="flex-shrink-0 w-12 text-center">
                        {getRankBadge(entry.rank)}
                      </div>

                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {entry.profile_picture_url ? (
                          <img
                            src={entry.profile_picture_url}
                            alt={entry.name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-purple-200"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                            <User className="w-8 h-8 text-white" />
                          </div>
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-lg text-gray-900 truncate">
                          {entry.name}
                        </div>
                        <div className="text-sm text-gray-600 truncate">
                          @{entry.username} • {entry.if_user_id}
                        </div>
                        {entry.bio && (
                          <div className="text-sm text-gray-500 mt-1 truncate">
                            {entry.bio}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {entry.session_count} session{entry.session_count !== 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Score */}
                      <div className="flex-shrink-0">
                        <div className={`px-4 py-2 rounded-lg bg-gradient-to-r ${getScoreColor(activeTab)} text-white text-center min-w-[80px]`}>
                          <div className="text-2xl font-bold">
                            {activeTab === 'iqi' && entry.avg_iqi}
                            {activeTab === 'oi' && entry.avg_oi}
                            {activeTab === 'msa' && `${entry.avg_spark}%`}
                          </div>
                          <div className="text-xs opacity-90">
                            {activeTab === 'iqi' && 'IQI'}
                            {activeTab === 'oi' && 'OI'}
                            {activeTab === 'msa' && 'Spark'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">No data available</p>
                  <p className="text-sm">
                    {activeTab === 'oi' && 'Users must opt-in to share OI scores'}
                    {activeTab === 'msa' && 'Users must opt-in to share MSA scores'}
                    {activeTab === 'iqi' && 'Complete sessions with IQI scores to appear on the leaderboard'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Box */}
        <div className="mt-12 max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Sparkles className="w-8 h-8 text-purple-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-lg text-purple-900 mb-2">About Community Rankings</h4>
                  <p className="text-gray-700 mb-3">
                    <strong>IQI (Interaction Quality Index)</strong> measures the quality of interactions across 5 dimensions: 
                    Clarity, Depth, Empathy, Reciprocity, and Transformative Potential. This score is always public to maintain 
                    community quality.
                  </p>
                  <p className="text-gray-700 mb-3">
                    <strong>OI (Originality Index)</strong> and <strong>MSA (Mirror-Spark Assessment)</strong> are opt-in metrics. 
                    Users control whether these scores appear publicly on their profiles and leaderboards.
                  </p>
                  <p className="text-gray-700">
                    Rankings update in real-time as Fountainheads complete sessions and calculate scores. 
                    Keep innovating to climb the leaderboard! 🚀
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default CommunityPage;
